import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { combineLatest, debounceTime, forkJoin, Observable, Subscription, take, tap } from 'rxjs';
import { SmallNumberPipe } from 'src/app/pipes/small-number/small-number.pipe';
import { AlbionImageService } from 'src/app/services/albion-image/albion-image.service';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { CheckPriceButtonComponent } from 'src/app/shared/components/check-price-button/check-price-button.component';
import { CopyTextButtonComponent } from 'src/app/shared/components/copy-text-button/copy-text-button.component';
import { getAllRoyalMarketLocations, LOCATIONS } from 'src/app/shared/constants/locations.constant';
import { getAllPriceTypes, PRICE_TYPES, SALES_TAXES, SETUP_FEES } from 'src/app/shared/constants/market.constant';
import { MarketHistoriesResponse, MarketResponse } from 'src/app/shared/models/ao-data-interfaces';
import { Item, JournalLoot } from 'src/app/shared/models/items.model';
import { Location } from 'src/app/shared/models/location.model';
import { PriceType } from 'src/app/shared/models/market.model';

@Component({
  selector: 'app-journals',
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    ReactiveFormsModule,
    FormsModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatTableModule,
    CopyTextButtonComponent,
    CheckPriceButtonComponent,
    MatCardModule,
    MatTooltipModule,
    MatInputModule,
    MatFormFieldModule,
  ],
  templateUrl: './journals.component.html',
  styleUrl: './journals.component.scss',
})
// MARK: JournalsComponent
export class JournalsComponent implements OnInit, OnDestroy {
  journalResults: JournalResult[] = [];
  selectedJournalResult: JournalResult | undefined = undefined;
  fullJournals: Item[] = [];
  filteredFullJournals: Item[] = [];
  emptyJournals: Item[] = [];
  allUniqueNames: string[] = [];
  journalGroups: string[] = [];
  prices: MarketResponse[] = [];
  histories: MarketHistoriesResponse[] = [];
  locations = getAllRoyalMarketLocations();
  priceTypes = getAllPriceTypes();
  scenarios: string[] = ['Buy Empty, Sell Mats', 'Buy Empty, Sell Full', 'Buy Full, Sell Mats'];
  dataSource: MatTableDataSource<string> = new MatTableDataSource<string>(this.journalGroups);
  enableRefreshPricesButton = true;
  private timeout: any;
  subscriptions: Subscription[] = [];

  journalsFormGroup = new FormGroup<JournalForm>({
    scenario: new FormControl('Buy Full, Sell Mats'),
    happiness: new FormControl(1.5),
    buyLocationForm: new FormControl(LOCATIONS.Caerleon),
    sellLocationForm: new FormControl(LOCATIONS.Caerleon),
    buyTypeForm: new FormControl(PRICE_TYPES.sell),
    sellTypeForm: new FormControl(PRICE_TYPES.sell),
    isPremium: new FormControl(true),
  });

  constructor(
    private jsonReaderService: JsonReaderService,
    private aoDataGetter: AoDataGetterService,
    public userSettingsService: UserSettingsService,
    public imageService: AlbionImageService,
    private cdr: ChangeDetectorRef
  ) {}

  // MARK: ngOnInit
  ngOnInit(): void {
    this.jsonReaderService.ensureLoaded();

    this.subscriptions.push(
      combineLatest([this.jsonReaderService.itemsData$]).subscribe(([items]) => {
        if (!items || items.length === 0) {
          return;
        }
        this.fullJournals = items.filter((x) => x.journalCraftItemFame && x.uniqueName.endsWith('_FULL'));
        this.filteredFullJournals = this.fullJournals;
        this.emptyJournals = items.filter((x) => x.journalCraftItemFame && x.uniqueName.endsWith('_EMPTY'));

        this.journalGroups = Array.from(new Set(this.fullJournals.map((x) => x.groupUniqueName)));

        const set = new Set<string>();

        this.fullJournals
          .flatMap((x) => x.journalLoots)
          .forEach((loot) => {
            if (loot?.uniqueName) {
              set.add(loot.uniqueName);
            }
          });

        this.allUniqueNames = [];
        this.allUniqueNames.push(...this.fullJournals.map((x) => x.uniqueName), ...this.emptyJournals.map((x) => x.uniqueName), ...Array.from(set));

        this.refreshPricesAndCreateResults();
      }),
      this.userSettingsService.userServer$.subscribe((server) => {
        if (this.allUniqueNames.length > 0) {
          this.refreshPricesAndCreateResults();
        }
      }),

      this.journalsFormGroup.valueChanges.pipe(debounceTime(200)).subscribe((value) => {
        this.createResults();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  // MARK: Table stuff
  setupDataSource() {
    this.dataSource = new MatTableDataSource<string>(this.journalGroups);
  }

  // MARK: updatePricesAndHistories
  updatePricesAndHistories(): Observable<{ prices: any; histories: any }> {
    return forkJoin({
      prices: this.aoDataGetter.getPrices(
        this.allUniqueNames,
        getAllRoyalMarketLocations().map((x) => x.name)
      ),
      histories: this.aoDataGetter.getHistory(
        this.allUniqueNames,
        undefined,
        undefined,
        getAllRoyalMarketLocations().map((x) => x.name)
      ),
    }).pipe(
      tap(({ prices, histories }) => {
        this.prices = prices;
        this.histories = histories;

        // Update the prices and histories for each journal result
        this.journalResults.forEach((journalResult) => {
          journalResult.prices = prices;
          journalResult.histories = histories;
        });
      })
    );
  }

  // MARK: Methods
  getJournalResultFromGroup(group: string, tier: number): JournalResult | undefined {
    return this.journalResults.find((x) => x.fullJournal.groupUniqueName === group && x.fullJournal.tier === tier);
  }

  removeFirstWord(text: string): string {
    // Remove everything after and including '('
    text = text.replace(/\(.*$/, '');

    // Remove the first word
    return text.split(' ').slice(1).join(' ');
  }

  selectJournalResult(group: string, tier: number) {
    this.selectedJournalResult = this.getJournalResultFromGroup(group, tier);
  }

  createResults() {
    const oldSelectedResult = this.selectedJournalResult?.fullJournal.uniqueName;
    this.journalResults = [];
    this.fullJournals.forEach((fullJournal) => {
      const emptyJournal = this.emptyJournals.find((x) => x.uniqueName === fullJournal.uniqueName.replace('_FULL', '_EMPTY'));
      if (emptyJournal) {
        this.journalResults.push(new JournalResult(fullJournal, emptyJournal, this.prices, this.histories, this.journalsFormGroup, this.aoDataGetter));
      }
    });
    this.selectedJournalResult = this.journalResults.find((x) => x.fullJournal.uniqueName === oldSelectedResult);

    this.cdr.markForCheck();
  }

  refreshPricesAndCreateResults() {
    this.enableRefreshPricesButton = false;
    this.cdr.markForCheck();
    this.updatePricesAndHistories()
      .pipe(take(1))
      .subscribe(({ prices, histories }) => {
        this.createResults();
        this.setupDataSource();
        setTimeout(() => {
          this.enableRefreshPricesButton = true;
          this.cdr.markForCheck();
        }, 5000);
      });
  }
  onPriceChange(uniqueName: string, type: 'buy' | 'sell', value: number) {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      if (type === 'buy') {
        if (this.journalsFormGroup.controls.buyTypeForm.value === PRICE_TYPES.buy) {
          this.prices.find((x) => x.item.uniqueName === uniqueName && x.city === this.journalsFormGroup.controls.buyLocationForm.value)!.buyPriceMax = value;
        }
        if (this.journalsFormGroup.controls.buyTypeForm.value === PRICE_TYPES.sell) {
          this.prices.find((x) => x.item.uniqueName === uniqueName && x.city === this.journalsFormGroup.controls.buyLocationForm.value)!.sellPriceMin = value;
        }
        if (this.journalsFormGroup.controls.buyTypeForm.value === PRICE_TYPES.average) {
          this.histories
            .find((x) => x.item?.uniqueName === uniqueName && x.location === this.journalsFormGroup.controls.buyLocationForm.value)!
            .data?.forEach((data) => {
              data.averagePrice = value;
            });
        }
      }
      if (type === 'sell') {
        if (this.journalsFormGroup.controls.sellTypeForm.value === PRICE_TYPES.buy) {
          this.prices.find((x) => x.item.uniqueName === uniqueName && x.city === this.journalsFormGroup.controls.sellLocationForm.value)!.buyPriceMax = value;
        }
        if (this.journalsFormGroup.controls.sellTypeForm.value === PRICE_TYPES.sell) {
          this.prices.find((x) => x.item.uniqueName === uniqueName && x.city === this.journalsFormGroup.controls.sellLocationForm.value)!.sellPriceMin = value;
        }
        if (this.journalsFormGroup.controls.sellTypeForm.value === PRICE_TYPES.average) {
          this.histories
            .find((x) => x.item?.uniqueName === uniqueName && x.location === this.journalsFormGroup.controls.sellLocationForm.value)!
            .data?.forEach((data) => {
              data.averagePrice = value;
            });
        }
      }
      this.createResults();
    }, 500); // 500ms delay
  }
}

// MARK: JournalResult
class JournalResult {
  readonly setupFee: number;
  readonly salesTax: number;
  readonly fullJournalBuyPrice: { price: number; date: Date; dailyAmount: number };
  readonly fullJournalBuySetupFee: number;
  readonly fullJournalBuyResult: number;
  readonly fullJournalSellPrice: { price: number; date: Date; dailyAmount: number };
  readonly fullJournalSellTax: number;
  readonly fullJournalSellSetupFee: number;
  readonly fullJournalSellResult: number;
  readonly emptyJournalBuyPrice: { price: number; date: Date; dailyAmount: number };
  readonly emptyJournalBuySetupFee: number;
  readonly emptyJournalBuyResult: number;
  readonly emptyJournalSellPrice: { price: number; date: Date; dailyAmount: number };
  readonly emptyJournalSellTax: number;
  readonly emptyJournalSellSetupFee: number;
  readonly emptyJournalSellResult: number;
  readonly results: { income: number; cost: number; result: number };
  readonly lootsWithSellPrices: LootResult[];
  readonly hasMissingPrices: boolean = false;
  constructor(
    public fullJournal: Item,
    public emptyJournal: Item,
    public prices: MarketResponse[],
    public histories: MarketHistoriesResponse[],
    public formGroup: FormGroup<JournalForm>,
    private aoDataGetter: AoDataGetterService
  ) {
    this.setupFee = SETUP_FEES.normal.value;
    this.salesTax = this.formGroup.controls.isPremium.value ? SALES_TAXES.premium.value : SALES_TAXES.normal.value;

    this.fullJournalBuyPrice = this.getPrice(
      this.fullJournal,
      this.formGroup.controls.buyLocationForm.value!,
      this.formGroup.controls.buyTypeForm.value!,
      'buy'
    );
    this.fullJournalBuySetupFee = this.formGroup.controls.buyTypeForm.value === PRICE_TYPES.sell ? 0 : this.fullJournalBuyPrice.price * this.setupFee;
    this.fullJournalBuyResult = -this.fullJournalBuyPrice.price - this.fullJournalBuySetupFee;

    this.fullJournalSellPrice = this.getPrice(
      this.fullJournal,
      this.formGroup.controls.sellLocationForm.value!,
      this.formGroup.controls.sellTypeForm.value!,
      'sell'
    );
    this.fullJournalSellTax = this.fullJournalSellPrice.price * this.salesTax;
    this.fullJournalSellSetupFee = this.formGroup.controls.sellTypeForm.value === PRICE_TYPES.buy ? 0 : this.fullJournalSellPrice.price * this.setupFee;
    this.fullJournalSellResult = this.fullJournalSellPrice.price - this.fullJournalSellTax - this.fullJournalSellSetupFee;

    this.emptyJournalBuyPrice = this.getPrice(
      this.emptyJournal,
      this.formGroup.controls.buyLocationForm.value!,
      this.formGroup.controls.buyTypeForm.value!,
      'buy'
    );
    this.emptyJournalBuySetupFee = this.formGroup.controls.buyTypeForm.value === PRICE_TYPES.sell ? 0 : this.emptyJournalBuyPrice.price * this.setupFee;
    this.emptyJournalBuyResult = -this.emptyJournalBuyPrice.price - this.emptyJournalBuySetupFee;

    this.emptyJournalSellPrice = this.getPrice(
      this.emptyJournal,
      this.formGroup.controls.sellLocationForm.value!,
      this.formGroup.controls.sellTypeForm.value!,
      'sell'
    );
    this.emptyJournalSellTax = this.emptyJournalSellPrice.price * this.salesTax;
    this.emptyJournalSellSetupFee = this.formGroup.controls.sellTypeForm.value === PRICE_TYPES.buy ? 0 : this.emptyJournalSellPrice.price * this.setupFee;
    this.emptyJournalSellResult = this.emptyJournalSellPrice.price - this.emptyJournalSellTax - this.emptyJournalSellSetupFee;

    this.lootsWithSellPrices = this.calculateLootsWithSellPrices();
    this.results = this.calculateResults();

    if (formGroup.controls.scenario.value === 'Buy Empty, Sell Mats') {
      this.hasMissingPrices = [
        this.emptyJournalBuyPrice.price,
        this.emptyJournalSellPrice.price,
        ...this.lootsWithSellPrices.filter((x) => x.loot.item).map((x) => x.price.price),
      ].some((x) => !(x > 0));
    } else if (formGroup.controls.scenario.value === 'Buy Empty, Sell Full') {
      this.hasMissingPrices = [this.fullJournalSellPrice.price, this.emptyJournalBuyPrice.price].some((x) => !(x > 0));
    } else if (formGroup.controls.scenario.value === 'Buy Full, Sell Mats') {
      this.hasMissingPrices = [
        this.fullJournalBuyPrice.price,
        this.emptyJournalSellPrice.price,
        ...this.lootsWithSellPrices.filter((x) => x.loot.item).map((x) => x.price.price),
      ].some((x) => !(x > 0));
    }
  }

  // MARK: calculateLootsWithSellPrices
  private calculateLootsWithSellPrices(): LootResult[] {
    if (!this.fullJournal.journalLoots || !this.formGroup.controls.sellLocationForm.value || !this.formGroup.controls.sellTypeForm.value) {
      return [];
    }

    return this.fullJournal.journalLoots.map(
      (loot) =>
        new LootResult(
          this.fullJournal,
          loot,
          this.prices,
          this.histories,
          this.formGroup,
          this.getPrice(loot.item, this.formGroup.controls.sellLocationForm.value!, this.formGroup.controls.sellTypeForm.value!, 'sell')
        )
    );
  }

  // MARK: calculateResults
  private calculateResults(): { income: number; cost: number; result: number } {
    const lootIncome = this.lootsWithSellPrices.reduce((acc, loot) => acc + loot.income, 0);
    const lootCost = this.lootsWithSellPrices.reduce((acc, loot) => acc + loot.cost, 0);

    const scenario = this.formGroup.controls.scenario.value;

    let income = 0;
    let cost = 0;

    if (scenario === 'Buy Empty, Sell Mats') {
      income = this.emptyJournalSellPrice.price + lootIncome;
      cost = this.emptyJournalBuyPrice.price + this.emptyJournalBuySetupFee + this.emptyJournalSellTax + this.emptyJournalSellSetupFee + lootCost;
    } else if (scenario === 'Buy Empty, Sell Full') {
      income = this.fullJournalSellPrice.price;
      cost = this.emptyJournalBuyPrice.price + this.emptyJournalBuySetupFee + this.fullJournalSellSetupFee + this.fullJournalSellTax;
    } else if (scenario === 'Buy Full, Sell Mats') {
      income = this.emptyJournalSellPrice.price + lootIncome;
      cost = this.fullJournalBuyPrice.price + this.fullJournalBuySetupFee + this.emptyJournalSellTax + this.emptyJournalSellSetupFee + lootCost;
    }

    return { income, cost, result: income - cost };
  }

  // MARK: getPrice
  getPrice(
    item: Item | undefined,
    location: Location,
    priceType: PriceType,
    operation: 'buy' | 'sell',
    daysForAverage = 7
  ): { price: number; date: Date; dailyAmount: number } {
    const averages = this.aoDataGetter.getAveragePriceAndAmount(item, location, this.histories, undefined, daysForAverage);

    if (operation === 'buy') {
      if (priceType === PRICE_TYPES.buy) {
        const price = this.aoDataGetter.getMinAndMaxPrice(item, location, this.prices).maxBuyPrice;
        return { price: price?.buyPriceMax ?? 0, date: price?.buyPriceMaxDate ?? new Date(0), dailyAmount: averages.averageAmount ?? 0 };
      }
      if (priceType === PRICE_TYPES.sell) {
        const price = this.aoDataGetter.getMinAndMaxPrice(item, location, this.prices).minSellPrice;
        return { price: price?.sellPriceMin ?? 0, date: price?.sellPriceMinDate ?? new Date(0), dailyAmount: averages.averageAmount ?? 0 };
      }
      if (priceType === PRICE_TYPES.average) {
        return { price: Math.ceil(averages.averagePrice ?? 0), date: new Date(), dailyAmount: averages.averageAmount ?? 0 };
      }
    }
    if (operation === 'sell') {
      if (priceType === PRICE_TYPES.buy) {
        const price = this.aoDataGetter.getMinAndMaxPrice(item, location, this.prices).maxBuyPrice;
        return { price: price?.buyPriceMax ?? 0, date: price?.buyPriceMaxDate ?? new Date(0), dailyAmount: averages.averageAmount ?? 0 };
      }
      if (priceType === PRICE_TYPES.sell) {
        const price = this.aoDataGetter.getMinAndMaxPrice(item, location, this.prices).minSellPrice;
        return { price: price?.sellPriceMin ?? 0, date: price?.sellPriceMinDate ?? new Date(0), dailyAmount: averages.averageAmount ?? 0 };
      }
      if (priceType === PRICE_TYPES.average) {
        const price = this.aoDataGetter.getAveragePriceAndAmount(item, location, this.histories);
        return { price: Math.floor(price.averagePrice ?? 0), date: new Date(), dailyAmount: price.averageAmount ?? 0 };
      }
    }
    return { price: 0, date: new Date(0), dailyAmount: 0 };
  }
  // get setupFee(): number {
  //   return SETUP_FEES.normal.value;
  // }

  // get salesTax(): number {
  //   if (this.formGroup.controls.isPremium.value) {
  //     return SALES_TAXES.premium.value;
  //   } else {
  //     return SALES_TAXES.normal.value;
  //   }
  // }

  // get fullJournalBuyPrice(): { price: number; date: Date; dailyAmount: number } {
  //   if (!this.formGroup.controls.buyLocationForm.value || !this.formGroup.controls.buyTypeForm.value) {
  //     return { price: 0, date: new Date(0), dailyAmount: 0 };
  //   }
  //   return this.getPrice(this.fullJournal, this.formGroup.controls.buyLocationForm.value, this.formGroup.controls.buyTypeForm.value, 'buy');
  // }

  // get fullJournalBuySetupFee(): number {
  //   if (this.formGroup.controls.buyTypeForm.value === PRICE_TYPES.sell) {
  //     return 0;
  //   }
  //   return this.fullJournalBuyPrice.price * this.setupFee;
  // }

  // get fullJournalSellPrice(): { price: number; date: Date; dailyAmount: number } {
  //   if (!this.formGroup.controls.sellLocationForm.value || !this.formGroup.controls.sellTypeForm.value) {
  //     return { price: 0, date: new Date(0), dailyAmount: 0 };
  //   }
  //   return this.getPrice(this.fullJournal, this.formGroup.controls.sellLocationForm.value, this.formGroup.controls.sellTypeForm.value, 'sell');
  // }

  // get fullJournalSellTax(): number {
  //   return this.fullJournalSellPrice.price * this.salesTax;
  // }

  // get fullFournalSellSetupFee(): number {
  //   if (this.formGroup.controls.sellTypeForm.value === PRICE_TYPES.buy) {
  //     return 0;
  //   }
  //   return this.fullJournalSellPrice.price * this.setupFee;
  // }

  // get emptyJournalBuyPrice(): { price: number; date: Date; dailyAmount: number } {
  //   if (!this.formGroup.controls.buyLocationForm.value || !this.formGroup.controls.buyTypeForm.value) {
  //     return { price: 0, date: new Date(0), dailyAmount: 0 };
  //   }
  //   return this.getPrice(this.emptyJournal, this.formGroup.controls.buyLocationForm.value, this.formGroup.controls.buyTypeForm.value, 'buy');
  // }

  // get emptyJournalBuySetupFee(): number {
  //   if (this.formGroup.controls.buyTypeForm.value === PRICE_TYPES.sell) {
  //     return 0;
  //   }
  //   return this.emptyJournalBuyPrice.price * this.setupFee;
  // }

  // get emptyJournalSellPrice(): { price: number; date: Date; dailyAmount: number } {
  //   if (!this.formGroup.controls.sellLocationForm.value || !this.formGroup.controls.sellTypeForm.value) {
  //     return { price: 0, date: new Date(0), dailyAmount: 0 };
  //   }
  //   return this.getPrice(this.emptyJournal, this.formGroup.controls.sellLocationForm.value, this.formGroup.controls.sellTypeForm.value, 'sell');
  // }

  // get emptyJournalSellTax(): number {
  //   return this.emptyJournalSellPrice.price * this.salesTax;
  // }

  // get emptyJournalSellSetupFee(): number {
  //   if (this.formGroup.controls.sellTypeForm.value === PRICE_TYPES.buy) {
  //     return 0;
  //   }
  //   return this.emptyJournalSellPrice.price * this.setupFee;
  // }

  // get results(): { income: number; cost: number; result: number } {

  //   const lootIncome = this.lootsWithSellPrices.reduce((acc, loot) => acc + loot.income, 0);
  //   const lootCost = this.lootsWithSellPrices.reduce((acc, loot) => acc + loot.cost, 0);

  //   const scenario = this.formGroup.controls.scenario.value;

  //   let income = 0;
  //   let cost = 0;

  //   if (scenario === 'Buy Empty, Sell Mats') {
  //     income = this.emptyJournalSellPrice.price + lootIncome;
  //     cost = this.emptyJournalBuyPrice.price + this.emptyJournalBuySetupFee + this.emptyJournalSellTax + this.emptyJournalSellSetupFee + lootCost;
  //   } else if (scenario === 'Buy Empty, Sell Full') {
  //     income = this.fullJournalSellPrice.price;
  //     cost = this.emptyJournalBuyPrice.price + this.emptyJournalBuySetupFee + this.fullFournalSellSetupFee + this.fullJournalSellTax;
  //   } else if (scenario === 'Buy Full, Sell Mats') {
  //     income = this.emptyJournalSellPrice.price + lootIncome;
  //     cost = this.fullJournalBuyPrice.price + this.fullJournalBuySetupFee + this.emptyJournalSellTax + this.emptyJournalSellSetupFee + lootCost;
  //   }

  //   return { income, cost, result: income - cost };
  // }

  // get lootsWithSellPrices(): LootResult[] {
  //   if (!this.fullJournal.journalLoots) {
  //     return [];
  //   }

  //   if (!this.formGroup.controls.buyLocationForm.value || !this.formGroup.controls.buyTypeForm.value) {
  //     return [];
  //   }

  //   return this.fullJournal.journalLoots
  //     .filter((loot) => this.formGroup.controls.sellLocationForm.value && this.formGroup.controls.sellTypeForm.value)
  //     .map((loot) => {
  //       return new LootResult(
  //         this.fullJournal,
  //         loot,
  //         this.prices,
  //         this.histories,
  //         this.formGroup,
  //         this.getPrice(loot.item!, this.formGroup.controls.sellLocationForm.value!, this.formGroup.controls.sellTypeForm.value!, 'sell')
  //       );
  //     });
  // }
}

// MARK: JournalForm
interface JournalForm {
  scenario: FormControl<string | null>;
  happiness: FormControl<number | null>;
  buyLocationForm: FormControl<Location | null>;
  sellLocationForm: FormControl<Location | null>;
  buyTypeForm: FormControl<PriceType | null>;
  sellTypeForm: FormControl<PriceType | null>;
  isPremium: FormControl<boolean | null>;
}

// MARK: LootResult
class LootResult {
  readonly setupFee: number;
  readonly salesTax: number;
  readonly amount: number;
  readonly silver: number;
  readonly date: Date;
  readonly salesTaxesCost: number;
  readonly setupFeeCost: number;
  readonly income: number;
  readonly cost: number;
  readonly result: number;
  readonly dailyAmount: number;
  constructor(
    public fullJournal: Item,
    public loot: JournalLoot,
    public prices: MarketResponse[],
    public histories: MarketHistoriesResponse[],
    public formGroup: FormGroup<JournalForm>,
    public price: { price: number; date: Date; dailyAmount: number }
  ) {
    this.setupFee = SETUP_FEES.normal.value;
    this.salesTax = this.formGroup.controls.isPremium.value ? SALES_TAXES.premium.value : SALES_TAXES.normal.value;
    this.amount = this.loot.amount * this.loot.probability * (this.fullJournal.baseLootAmount ?? 0) * (this.formGroup.controls.happiness.value ?? 1);
    this.silver = Math.max(this.amount, 1) * this.loot.silverAmount;
    this.date = this.price.date;
    this.salesTaxesCost = this.price.price * this.amount * this.salesTax;
    this.setupFeeCost = this.formGroup.controls.sellTypeForm.value === PRICE_TYPES.sell ? this.price.price * this.amount * this.setupFee : 0;
    this.income = this.price.price * this.amount + this.silver;
    this.cost = this.salesTaxesCost + this.setupFeeCost;
    this.result = this.income - this.cost;
    this.dailyAmount = this.price.dailyAmount;
  }

  // get setupFee(): number {
  //   return SETUP_FEES.normal.value;
  // }

  // get salesTax(): number {
  //   if (this.formGroup.controls.isPremium.value) {
  //     return SALES_TAXES.premium.value;
  //   } else {
  //     return SALES_TAXES.normal.value;
  //   }
  // }

  // get amount(): number {
  //   return this.loot.amount * this.loot.probability * (this.fullJournal.baseLootAmount ?? 0) * (this.formGroup.controls.happiness.value ?? 1);
  // }

  // get silver(): number {
  //   return (
  //     (this.loot.amount ?? 1) *
  //     this.loot.probability *
  //     (this.fullJournal.baseLootAmount ?? 0) *
  //     (this.formGroup.controls.happiness.value ?? 1) *
  //     this.loot.silverAmount
  //   );
  // }
  // get date(): Date {
  //   return this.price.date;
  // }
  // get salesTaxesCost(): number {
  //   return this.price.price * this.amount * this.salesTax;
  // }
  // get setupFeeCost(): number {
  //   return this.formGroup.controls.sellTypeForm.value === PRICE_TYPES.sell ? this.price.price * this.amount * this.setupFee : 0;
  // }

  // get income(): number {
  //   return this.price.price * this.amount + this.silver;
  // }

  // get cost(): number {
  //   return this.salesTaxesCost + this.setupFeeCost;
  // }

  // get result(): number {
  //   return this.income - this.cost;
  // }

  // get dailyAmount(): number {
  //   return this.price.dailyAmount;
  // }
}
